import { useSelector } from "react-redux";
import { Bell, Mail, Pencil, Smartphone } from "lucide-react";
import { RazorNumberFormat } from "../../../../components/razor-reuse";
import RichTextContent from "../../../../components/fragment/rich-text-editor/RichTextContent";
import RootState from "../../../../redux/type";
import {
  CHANNEL_META,
  CreateNotificationForm,
  NotificationChannelType,
  RECIPIENT_TYPE_META,
} from "../types";
import { formatRuleValue, isCountryRule } from "./AudienceFilterRules";
import { useNationalityCountries } from "./useNationalityCountries";
import WizardActionBar from "./WizardActionBar";

const CHANNEL_ICON: Record<NotificationChannelType, React.ReactNode> = {
  email: <Mail className="size-[16px] text-[#734792]" />,
  push: <Bell className="size-[16px] text-[#734792]" />,
  in_app: <Smartphone className="size-[16px] text-[#734792]" />,
};

function ReviewCard({
  label,
  onEdit,
  children,
}: {
  label: string;
  onEdit: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="bg-white border border-[#E4E4E7] rounded-[8px] p-[20px]">
      <div className="flex items-center justify-between">
        <p className="text-[11px] font-semibold tracking-wide text-[#71717A] uppercase">
          {label}
        </p>
        <button
          type="button"
          onClick={onEdit}
          className="inline-flex items-center gap-[4px] text-[13px] font-medium text-[#734792]"
        >
          <Pencil className="size-[12px]" />
          Edit
        </button>
      </div>
      <div className="mt-[12px]">{children}</div>
    </div>
  );
}

interface ReviewStepProps {
  form: CreateNotificationForm;
  onEditChannel: () => void;
  onEditConfigure: () => void;
  onCancel: () => void;
  onSaveDraft: () => void;
  onCreate: () => void;
  creating?: boolean;
  submitLabel:string
}

export default function ReviewStep({
  form,
  onEditChannel,
  onEditConfigure,
  onCancel,
  onSaveDraft,
  onCreate,
  submitLabel ='Submit',
  creating,
}: ReviewStepProps) {
  const { audience_preview_count, audience_csv_result } = useSelector(
    (state: RootState) => state.notification_campaign,
  );

  // Edit mode opens straight on this step, so fetch the list here too to show
  // country names rather than iso3 codes.
  const { countries } = useNationalityCountries(
    form.audience_type === "filter" &&
      form.filter_rules.some((rule) => isCountryRule(rule.type)),
  );

  const estimatedUsers =
    form.audience_type === "filter"
      ? audience_preview_count ?? 0
      : form.audience_type === "csv_upload"
        ? audience_csv_result?.matched_user_ids?.length ?? 0
        : undefined;

  return (
    <div className="mt-[16px]">
      <p className="text-[12px] font-semibold tracking-wide text-[#734792] uppercase">
        Step 3 of 4
      </p>
      <p className="text-[20px] font-bold text-[#1E1E1E] mt-[4px]">
        Review before sending
      </p>
      <p className="text-[13px] text-[#758089] mt-[4px]">
        Review every detail below before confirming. Click Edit on any section to make changes.
      </p>

      <div className="mt-[24px] space-y-[16px]">
        <ReviewCard label="Delivery channel" onEdit={onEditChannel}>
          <div className="flex flex-wrap gap-[12px]">
            {form.channels.map((selection) => (
              <div
                key={selection.channel}
                className="flex items-center gap-[10px] rounded-[8px] border border-[#E4E4E7] px-[14px] py-[10px]"
              >
                {CHANNEL_ICON[selection.channel]}
                <div>
                  <p className="text-[13px] font-bold text-[#1E1E1E]">
                    {CHANNEL_META[selection.channel].label}
                  </p>
                  <p className="text-[12px] text-[#758089]">
                    {CHANNEL_META[selection.channel].description} —{" "}
                    {RECIPIENT_TYPE_META[selection.recipient_type]}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </ReviewCard>

        <ReviewCard label="Notification message" onEdit={onEditConfigure}>
          <p className="text-[14px] font-bold text-[#1E1E1E]">{form.title}</p>
          <RichTextContent html={form.body} className="text-[13px] text-[#758089] mt-[4px]" />
        </ReviewCard>

        <ReviewCard label="Action button" onEdit={onEditConfigure}>
          {form.action_button_enabled && form.action_button_label ? (
            <div>
              <p className="text-[14px] font-bold text-[#1E1E1E]">
                {form.action_button_label}
              </p>
              <p className="text-[13px] text-[#758089] mt-[4px]">
                {form.action_button_url}
              </p>
            </div>
          ) : (
            <p className="text-[13px] text-[#A1A1AA]">No action button</p>
          )}
        </ReviewCard>

        <ReviewCard label="Target audience" onEdit={onEditConfigure}>
          {form.audience_type === "all" && (
            <p className="text-[13px] text-[#1E1E1E]">All eligible users</p>
          )}
          {form.audience_type === "csv_upload" && (
            <p className="text-[13px] text-[#1E1E1E]">
              Uploaded list: {form.uploaded_file_name || "no file uploaded"}
            </p>
          )}
          {form.audience_type === "filter" && (
            <div className="space-y-[4px]">
              {form.filter_rules.length < 1 ? (
                <p className="text-[13px] text-[#A1A1AA]">No targeting rules added</p>
              ) : (
                form.filter_rules.map((rule) => (
                  <p key={rule.id} className="text-[13px] text-[#1E1E1E]">
                    <span className="text-[#758089]">{rule.type}:</span>{" "}
                    {formatRuleValue(
                      rule.value,
                      isCountryRule(rule.type) ? countries : undefined,
                    )}
                  </p>
                ))
              )}
            </div>
          )}
          {estimatedUsers !== undefined && (
            <p className="mt-[12px] inline-flex items-center gap-[6px] text-[13px] font-bold text-[#16A34A]">
              <span className="size-[6px] rounded-full bg-[#16A34A]" />
              {RazorNumberFormat(estimatedUsers, { hideDecimal: true, hideSymbol: true })}{" "}
              estimated users
            </p>
          )}
        </ReviewCard>
      </div>

      <WizardActionBar
        onCancel={onCancel}
        onSaveDraft={onSaveDraft}
        onPrimary={onCreate}
        primaryLabel={`${submitLabel}  Notification`}
        primaryLoading={creating}
      />
    </div>
  );
}
